import { useCallback } from 'react';
import type { FC, KeyboardEvent } from 'react';
import { Flex } from '@chakra-ui/react';
import { useProductAvailabilityMessages } from '../../../../hooks/useProductAvailabilityMessages';
import { useRefDebounce } from '../../../../hooks/useRefDebounce';
import { QuantityInput } from '../../../../ui-components';
import { MAX_QUANTITY_INPUT_NUMBER } from '../../../../ui-components/inputs/quantity-input/const';
import { isExtend } from '../../../../utils/product-utils';
import { useUpdateCartItems } from '../../contexts/useUpdateCartItems';
import { ProductAvailabilityMessages } from './ProductAvailabilityMessages';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';

interface Props {
    productItem: ProductItemEditor;
}

export const CartQuantityBox: FC<Props> = ({ productItem }) => {
    const { updateProductItemLocally, updateProductItemRemote } = useUpdateCartItems();
    const { availableToSell } = useProductAvailabilityMessages({ productItem });
    const updateProductItemRemoteDebounced = useRefDebounce(updateProductItemRemote, 400);
    const isExtendItemQuantityBox = isExtend(productItem);

    const onQuantityChange = useCallback(
        (value: number) => {
            if (value > 0) {
                const updatedValue = {
                    ...productItem,
                    quantityEditor: isExtendItemQuantityBox
                        ? Math.min(value, availableToSell)
                        : value,
                };

                updateProductItemLocally(updatedValue);
                updateProductItemRemoteDebounced(updatedValue);
            }
        },
        [
            updateProductItemLocally,
            productItem,
            updateProductItemRemoteDebounced,
            isExtendItemQuantityBox,
            availableToSell,
        ],
    );

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>, value: number): void => {
        if (e.key === 'Enter') {
            onQuantityChange(value);
        }
    };

    return (
        <Flex
            direction="column"
            justifyContent="space-between"
            alignItems={{ 'lg-sm': 'end' }}
            gap={2}
        >
            <QuantityInput
                id={`cart-item-quantity-${productItem.productId}`}
                onChange={onQuantityChange}
                maxValue={
                    productItem.c_online ? Math.min(availableToSell, MAX_QUANTITY_INPUT_NUMBER) : 0
                }
                value={productItem.quantityEditor}
                onBlur={onQuantityChange}
                onKeyDown={onKeyDown}
                strictMaxValue={isExtendItemQuantityBox}
            />
            <ProductAvailabilityMessages productItem={productItem} />
        </Flex>
    );
};
